/**
 * Workforce Grid column model.
 *
 * Each column is a *header* that buckets one or more raw case-record states.
 * State matching is case-insensitive so backends emitting "Human_Audit" or
 * "human_audit" both land in the same bucket.
 *
 * Call-state routing (AI Call / Human Call) is special: the "Needed" vs
 * "In Progress" split is driven by whether the case has an `active_call_id`,
 * not by a distinct backend state. Human Audit is split the same way: an
 * audit-state case whose `status_flags.human_audit_in_progress` is set (an
 * auditor pressed "Start Human Audit") lands in "Human Audit In Progress"
 * instead of "Human Audit Needed". See `findColumnForCase` below.
 *
 * The map is TOTAL: `findColumnForCase` always resolves a column. States with
 * no bucket of their own — and cases whose state is missing entirely — land in
 * the catch-all `other` column, so every case the fetch returns is rendered
 * somewhere and the grid's counts describe exactly what's on screen.
 *
 * To add a new column or move a state, edit `WORKFORCE_COLUMNS` — no other
 * change is needed; the grid iterates this array directly.
 */

export interface WorkforceColumn {
  id: string;
  label: string;
  states: string[];
}

export const WORKFORCE_COLUMNS: WorkforceColumn[] = [
  {
    id: 'ai_call_needed',
    label: 'AI Call Needed',
    // `ai_call` is forked between this column and `ai_in_progress` based on
    // `active_call_id` — see `findColumnForCase`.
    states: [],
  },
  {
    id: 'ai_in_progress',
    label: 'AI In Progress',
    // Backend may also emit `ai_call_in_progress` as a distinct state on
    // older workflows — accept it as a synonym so it lands here too.
    // `awaiting_transfer_outcome` is refill's AI call that has handed the
    // patient to a Genesys queue and waits for the transfer's outcome.
    states: ['ai_call_in_progress', 'awaiting_transfer_outcome'],
  },
  {
    id: 'human_audit_needed',
    label: 'Human Audit Needed',
    // Accept every audit-needed flavor: legacy `human_audit*`, generic
    // `eligibility_check_human_audit`, unified MedBV `ec_human_audit`, and
    // the primary/secondary MedBV variants. Forked into
    // `human_audit_in_progress` when the audit has been started — see
    // `findColumnForCase`.
    states: [
      'human_audit_needed',
      'human_audit',
      'eligibility_check_human_audit',
      'ec_human_audit',
      'primary_eligibility_check_human_audit',
      'secondary_eligibility_check_human_audit',
      'primary_human_audit',
      'secondary_human_audit',
    ],
  },
  {
    id: 'human_audit_in_progress',
    label: 'Human Audit In Progress',
    // No backend state of its own: an audit-needed case is forked here by
    // `status_flags.human_audit_in_progress` — see `findColumnForCase`.
    states: [],
  },
  {
    id: 'human_call_needed',
    label: 'Human Call Needed',
    // `human_call` is forked between this column and `human_call_in_progress`
    // based on `active_call_id` — see `findColumnForCase`. Refill's
    // `human_call_needed` has no call to fork on and always lands here.
    states: ['human_call_needed'],
  },
  {
    id: 'human_call_in_progress',
    label: 'Human Call In Progress',
    states: [],
  },
  {
    id: 'missing_data',
    label: 'Missing Data',
    // `human_step_missing_info` is PBM/MedBV; `input_audit_needed` is the
    // transportation-workflow equivalent — same semantic ("case is waiting
    // on missing input data"), grouped here to avoid a transportation-only
    // 7th column.
    states: ['human_step_missing_info', 'input_audit_needed'],
  },
  {
    id: 'other',
    label: 'Other',
    // Catch-all. Deliberately empty: it contributes nothing to
    // `COLUMN_BY_STATE` and is only ever reached as `findColumnForCase`'s
    // fallback, so any state without a bucket of its own lands here.
    states: [],
  },
];

/** The catch-all column. Every state without an explicit bucket resolves here.
 *  Internal — callers reach it through `findColumnForCase`, never directly. */
const OTHER_COLUMN: WorkforceColumn = WORKFORCE_COLUMNS[WORKFORCE_COLUMNS.length - 1];

// state → column lookup, computed once. Normalises on both sides so callers
// don't have to think about casing.
const COLUMN_BY_STATE: Map<string, WorkforceColumn> = (() => {
  const map = new Map<string, WorkforceColumn>();
  for (const col of WORKFORCE_COLUMNS) {
    for (const s of col.states) map.set(s.toLowerCase(), col);
  }
  return map;
})();

const COLUMN_BY_ID: Map<string, WorkforceColumn> = new Map(
  WORKFORCE_COLUMNS.map(col => [col.id, col]),
);

export interface CaseColumnInput {
  state: string | undefined | null;
  /** Set when the case has a live call in progress. Drives the AI/Human
   *  "Needed" vs "In Progress" fork. */
  activeCallId?: string | null;
  /** case_record.status_flags.human_audit_in_progress. Drives the Human Audit
   *  "Needed" vs "In Progress" fork. */
  humanAuditInProgress?: boolean;
}

/** A "Needed" / "In Progress" column pair, picked by a per-case signal. */
interface ColumnFork {
  needed: string;
  inProgress: string;
}

// Call states fork on `active_call_id`.
const CALL_STATE_FORKS: Record<string, ColumnFork | undefined> = {
  ai_call: { needed: 'ai_call_needed', inProgress: 'ai_in_progress' },
  human_call: { needed: 'human_call_needed', inProgress: 'human_call_in_progress' },
};

// Every audit-needed state forks on `status_flags.human_audit_in_progress`.
const HUMAN_AUDIT_FORK: ColumnFork = {
  needed: 'human_audit_needed',
  inProgress: 'human_audit_in_progress',
};

function forkColumn(fork: ColumnFork, inProgress: boolean): WorkforceColumn {
  return COLUMN_BY_ID.get(inProgress ? fork.inProgress : fork.needed) ?? OTHER_COLUMN;
}

/**
 * Resolve the workforce column for a case. Handles the call-state fork
 * (active call → "In Progress", otherwise → "Needed") before falling back
 * to the static state→column map, then forks an audit-needed case into
 * "Human Audit In Progress" when its audit has been started.
 *
 * Total — never returns null. Anything unrecognised, including a missing
 * state, resolves to `OTHER_COLUMN`.
 */
export function findColumnForCase(input: CaseColumnInput): WorkforceColumn {
  const state = input.state?.toLowerCase();
  if (!state) return OTHER_COLUMN;

  const callFork = CALL_STATE_FORKS[state];
  if (callFork) return forkColumn(callFork, !!input.activeCallId);

  const col = COLUMN_BY_STATE.get(state) ?? OTHER_COLUMN;
  if (col.id === 'human_audit_needed') {
    return forkColumn(HUMAN_AUDIT_FORK, !!input.humanAuditInProgress);
  }
  return col;
}
